// dirty.ts — the SPA's two rules for dirty values, in one place.
//
// A dirty value is one an experiment's dirty mark says is not comparable to
// the rest (a changed eval protocol, leakage, a scorer bug). The rules:
//
//   1. It never counts toward a headline: no best, no ★, no "headline best",
//      no baseline, no trend or regression marker is ever computed from it.
//      A headline whose only candidates are dirty has no value (—).
//      There is no switch that puts dirty values back in.
//   2. Wherever it is drawn it is struck through (and faded), with the
//      marks' reasons on hover and an aria-label "dirty: <reason>", so the
//      state is never carried by styling alone. DirtyValue.tsx renders that
//      for HTML; SVG call sites use dirtySvgProps.
import type { DirtyRef } from '../api';

/** Opacity of a dirty value, on top of the strikethrough. */
export const DIRTY_OPACITY = 0.5;

/** Inline style for a dirty value in HTML. */
export const DIRTY_STYLE = { textDecoration: 'line-through', opacity: DIRTY_OPACITY } as const;

/** The marks' reasons, de-duplicated, joined for one line. */
export function dirtyReasons(refs: DirtyRef[] | undefined): string {
  const seen = new Set<string>();
  for (const r of refs ?? []) seen.add(r.reason);
  return Array.from(seen).join('; ');
}

/** The accessible label: "dirty: <reason>". */
export function dirtyLabel(refs: DirtyRef[] | undefined): string {
  return `dirty: ${dirtyReasons(refs) || 'marked not comparable'}`;
}

/** Tooltip lines naming why a value is dirty; '' for a clean one. */
export function dirtyTitle(refs: DirtyRef[] | undefined): string {
  if (!refs || refs.length === 0) return '';
  return refs
    .map((r) => `dirty (mark #${r.id}${r.marked_by ? `, ${r.marked_by}` : ''}): ${r.reason}`)
    .join('\n');
}

/** Props for an SVG <text> that renders a dirty value. */
export function dirtySvgProps(refs: DirtyRef[] | undefined): {
  style: { textDecoration: string };
  opacity: number;
  'aria-label': string;
} {
  return {
    style: { textDecoration: 'line-through' },
    opacity: DIRTY_OPACITY,
    'aria-label': dirtyLabel(refs),
  };
}

/**
 * True when a commit has nothing comparable to show: it carries at least
 * one of the metrics in question and every one of them is dirty. `only`
 * narrows "the metrics in question" (the graph's pinned chips); empty or
 * absent means all the commit's metrics. A commit with any clean value is
 * comparable on that value, so it is not incomparable.
 */
export function incomparable(
  metrics: Record<string, number> | undefined,
  dirty: Record<string, unknown> | undefined,
  only?: ReadonlySet<string>,
): boolean {
  const names = Object.keys(metrics ?? {}).filter((n) => !only || only.size === 0 || only.has(n));
  return names.length > 0 && names.every((n) => !!dirty?.[n]);
}

export interface Candidate {
  value: number;
  dirty?: boolean;
}

/**
 * The best clean value among `candidates` (max when `higher`, else min), or
 * undefined when none is clean. Dirty candidates never win, however good.
 */
export function cleanBest(candidates: Candidate[], higher: boolean): number | undefined {
  let best: number | undefined;
  for (const c of candidates) {
    if (c.dirty) continue;
    if (best === undefined || (higher ? c.value > best : c.value < best)) best = c.value;
  }
  return best;
}
